/**
 * useElectionCategories Composable
 *
 * Extracts election tabs from the ForecastTrader category tree API.
 * Provides dynamic tab data for the Election Hub UI.
 */
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import { useGetPredictionCategoryTree } from '@/api/prediction.queries';

/** Both old and new category tree IDs for the US Elections parent node */
const US_ELECTIONS_PARENT_IDS = new Set(['g7428', 'g137821']);

/**
 * Election tab data extracted from category tree
 */
export interface ElectionTab {
  readonly id: string;
  readonly label: string;
  readonly name: string;
}

/**
 * Return type for useElectionCategories composable
 */
export interface UseElectionCategoriesReturn {
  /** Dynamic election tabs from API */
  readonly electionTabs: ComputedRef<ElectionTab[]>;
  /** Loading state */
  readonly isLoading: ComputedRef<boolean>;
  /** Error state */
  readonly error: ComputedRef<Error | null>;
}

/**
 * Format category name to short tab label
 * "United States Gubernatorial" -> "Governor"
 * "United States Senate" -> "Senate"
 */
function formatCategoryLabel(name: string): string {
  const mapping: Record<string, string> = {
    'United States Gubernatorial': 'Governor',
    'United States Senate': 'Senate',
    'United States House': 'House',
  };
  return mapping[name] || name;
}

/**
 * Composable for fetching dynamic election tabs from category tree API
 *
 * @example
 * ```vue
 * <script setup>
 * const { electionTabs, isLoading } = useElectionCategories()
 * </script>
 * <template>
 *   <Tab v-for="tab in electionTabs" :key="tab.id" :value="tab.id">
 *     {{ tab.label }}
 *   </Tab>
 * </template>
 * ```
 */
export function useElectionCategories(): UseElectionCategoriesReturn {
  const { data: categoryTree, isLoading, error } = useGetPredictionCategoryTree();

  const electionTabs = computed<ElectionTab[]>(() => {
    if (!categoryTree.value?.categories) return [];

    // Fixed display order: Governor, Senate, House
    const tabOrder: readonly string[] = ['Governor', 'Senate', 'House'];

    const apiTabs = categoryTree.value.categories
      .filter((cat) => cat.parent_id != null && US_ELECTIONS_PARENT_IDS.has(cat.parent_id))
      .map((cat) => ({
        id: cat.id,
        label: formatCategoryLabel(cat.name),
        name: cat.name,
      }))
      .filter((tab) => tabOrder.includes(tab.label))
      .sort((a, b) => tabOrder.indexOf(a.label) - tabOrder.indexOf(b.label));

    return [...apiTabs, { id: '__all__', label: 'All By State', name: 'All By State' }];
  });

  return {
    electionTabs,
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
  };
}
